import { shallowEqual, useSelector } from 'react-redux';
import type { Table } from 'schema-dts';
import { toSignificant } from '@simplywallst/formatting';
import type { State as CompanyState } from '@/pages/CompanyReport/redux/interface';
import DocumentSchema from '@/components/DocumentSchema';
import { getStabilityAndGrowthOfPaymentsRawDataSelector } from '@/pages/CompanyReport/components/Dividend/components/StabilityAndGrowthOfPayments/redux/selectors';
import { StabilityAndGrowthTable } from './styled';
import type { ViewMode } from '@/pages/CompanyReport/enum';
import ListTruncator from '@/components/ListTruncator';

interface Props {
  companyName: string;
  viewMode: ViewMode;
}
interface ReduxState {
  company: CompanyState;
}

function selector(state: ReduxState) {
  const rawData = getStabilityAndGrowthOfPaymentsRawDataSelector(state, {});

  return {
    pastAnnualizedDividendPayouts: rawData.pastAnnualizedDividendPayouts,
    futureDividendEstimates: rawData.futureDividendEstimates,
  };
}

export const StabilityAndGrowthOfPaymentsTable = ({
  companyName,
  viewMode,
}: Props) => {
  const { pastAnnualizedDividendPayouts, futureDividendEstimates } =
    useSelector<Parameters<typeof selector>[0], ReturnType<typeof selector>>(
      selector,
      shallowEqual
    );
  const header = `${companyName} Dividend History`;
  const caption = `How stable has ${companyName}'s dividend per share been in the past?`;
  const data = futureDividendEstimates.concat(pastAnnualizedDividendPayouts);

  const Table = (
    <>
      <DocumentSchema<Table>
        item={{
          '@context': 'https://schema.org',
          '@type': 'Table',
          headline: header,
          about: caption,
        }}
      />
      <StabilityAndGrowthTable
        columnTextAlignments="l"
        data-cy-id="dividend-stability-and-growth-of-payments-table"
      >
        <caption data-cy-id="dividend-stability-and-growth-of-payments-table-caption">
          {caption}
        </caption>
        <thead>
          <tr>
            <th>Month</th>
            <th>Dividend Per Share (annual)</th>
            <th>Avg. Yield (%)</th>
          </tr>
        </thead>
        <tbody>
          {data.map((item, i) => (
            <tr
              data-cy-id={`dividend-stability-and-growth-of-payments-table-row-${i}`}
              key={`future-estimates-row-${i}`}
            >
              <td>{new Date(item.dateKey).toLocaleDateString()}</td>
              <td>
                {item.data[0] === 0
                  ? '-'
                  : parseFloat(toSignificant(item.data[0], 2)).toLocaleString()}
              </td>
              <td>
                {parseFloat(toSignificant(item.data[1], 1)).toLocaleString() +
                  '%'}
              </td>
            </tr>
          ))}
        </tbody>
      </StabilityAndGrowthTable>
    </>
  );

  return data.length > 9 ? (
    <div data-cy-id="dividend-stability-and-growth-of-payments-truncator">
      <ListTruncator viewMode={viewMode} minHeight={430}>
        {Table}
      </ListTruncator>
    </div>
  ) : (
    Table
  );
};
